import pronteerLogoFull from '@/assets/pronteer-logo-full.svg'
import { CircleAlert, FaceId, Lock, RefreshCw } from '@/components/icons'
import { Button } from '@/components/ui/button'
import { useBiometryName } from '@/lib/face-id'

interface FaceIdStatusScreenProps {
  variant: 'failed' | 'lockedOut'
  isPrompting?: boolean
  onRetry?: () => void
  // Present only when an app passcode is set — offers passcode entry as a
  // fallback (the sole action on 'lockedOut', where retrying is impossible).
  onUsePasscode?: () => void
}

/**
 * Resting lock screen after a biometric attempt: 'failed' (auth failure or
 * canceled sheet — offers Retry) or 'lockedOut' (biometry disabled by iOS
 * after too many failures — recovery requires locking and unlocking the
 * device, so only the passcode fallback is offered, when available).
 */
export function FaceIdStatusScreen({
  variant,
  isPrompting = false,
  onRetry,
  onUsePasscode,
}: FaceIdStatusScreenProps) {
  const biometryName = useBiometryName()

  return (
    <div className="fixed inset-0 z-9999 flex flex-col items-center bg-surface-L0 px-6 pb-[var(--safe-bottom)] pt-[var(--safe-top)]">
      <img src={pronteerLogoFull} alt="Pronteer" className="mt-14 h-[46px] w-auto" />
      <div className="flex flex-1 items-center justify-center">
        <div className="flex w-full max-w-[215px] flex-col items-center gap-4 text-center">
          <div className="relative">
            {/* Exact Figma fills — the design system has no tokens for these tints. */}
            <div className="flex size-20 items-center justify-center rounded-full bg-[#EFE2EC] text-content-brand-bold">
              <FaceId className="size-12" />
            </div>
            <div className="absolute -right-3 -top-3 flex size-9 items-center justify-center rounded-full bg-[#FAF6F9] text-content-primary-bold">
              {variant === 'failed' ? (
                <CircleAlert className="size-4" />
              ) : (
                <Lock className="size-4" />
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <h1 className="text-base font-semibold leading-6 tracking-[-0.5px] text-content-primary-bold">
              {variant === 'failed'
                ? `${biometryName} didn't recognize you.`
                : 'Pronteer is locked.'}
            </h1>
            <p className="text-sm leading-5 text-content-primary-subtle">
              {variant === 'failed'
                ? 'Make sure your face is clearly visible and try again.'
                : `To re-enable ${biometryName}, lock and then unlock your device.`}
            </p>
          </div>
          {variant === 'failed' && (
            <Button
              variant="brand"
              className="h-auto w-full gap-1.5 rounded-md px-4 py-2.5 text-xs [&_svg]:size-3"
              disabled={isPrompting}
              onClick={onRetry}
            >
              <RefreshCw />
              Retry
            </Button>
          )}
          {onUsePasscode && (
            <Button
              variant="secondary"
              className="h-auto w-full rounded-md px-4 py-2.5 text-xs"
              disabled={isPrompting}
              onClick={onUsePasscode}
            >
              Use passcode
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
